import {useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useVariables} from '../../../../assets/objects/Variables';
import * as css from '../WhatsAppEditorWrapper.css';
import {convertVariablesToHtml} from '../../../../component/textEditor/Variables';
import sanitizeHtml from '../../../../library/sanitizeHtml';
import {toCamelCase} from '../../../../functions/camelSnakeCaseConversion';
import {DEFAULT_LANG} from '../../../../globalVariables/locals';

/**
 * Custom hook for managing WhatsApp messages
 * Returns the text and JSX elements for prefix and suffix
 */
export const useWhatsAppMessage = (whatsappTemplate = null, whatsappPrefix, whatsappSuffix, whatsappButtons, maxLines) => {
    const {defaultVariables} = useVariables();
    const {i18n} = useTranslation('branding');
    const {t} = useTranslation('variables');
    const [showWhatsAppWarning, setShowWhatsAppWarning] = useState(false);
    
    // Getting data from Redux store
    const currentLang = useSelector(state => state.branding.currentLang) || DEFAULT_LANG;
    
    const direction = currentLang !== 'he' ? 'ltr' : 'rtl';
    const tBranding = i18n.getFixedT(currentLang, 'branding');

    const getTemplatePrefix = () => {
        if(!whatsappPrefix) return null;
        
        return <css.prefix direction={direction}
            dangerouslySetInnerHTML={sanitizeHtml(convertVariablesToHtml(whatsappPrefix, defaultVariables, t))}
        />;
    };

    const getTemplateSuffix = () => {
        if(!whatsappSuffix) return null;
        return <css.suffix direction={direction}>
            <div dangerouslySetInnerHTML={sanitizeHtml(convertVariablesToHtml(whatsappSuffix, defaultVariables, t))} />
            {whatsappButtons && <css.suffixButtons>
                {whatsappButtons.map((button, index) => (
                    <css.button key={index}>
                        {t(toCamelCase((button.value).replace(/[[\]]/g, '')))}
                    </css.button>
                ))}
            </css.suffixButtons>}
        </css.suffix>;
    };

    const prefix = whatsappTemplate ? <>
        {showWhatsAppWarning && <css.whatsappWarning direction={direction}>{tBranding('whatsappLineBreakWarning', {maxLines})}</css.whatsappWarning>}
        {getTemplatePrefix()}
    </> : null;

    const suffix = getTemplateSuffix();

    const validateLineCount = (newText) => {
        if(!whatsappTemplate) return false;
        const lineCount = (newText.match(/<p[^>]*>/g) || []).length;
        const isDisabled = lineCount > maxLines;
        setShowWhatsAppWarning(isDisabled);
        return isDisabled;
    };
    
    return {
        prefix,
        suffix,
        showWhatsAppWarning,
        validateLineCount,
        getTemplatePrefix,
        getTemplateSuffix,
        direction
    };
};